import { useMemo } from 'react';
import { DROPDOWN_ITEM_DEFAULT_HEIGHT, DROPDOWN_PADDING_Y } from 'HubStyleTokens/sizes';
import getLang from 'I18n/utils/getLang';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIInlineLabel from 'UIComponents/form/UIInlineLabel';
import UISelect from 'UIComponents/input/UISelect';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UICheckbox from 'UIComponents/input/UICheckbox';
import { jsx as _jsx } from "react/jsx-runtime";
const MENU_ITEM_HEIGHT = parseInt(DROPDOWN_ITEM_DEFAULT_HEIGHT, 10);
const MENU_PADDING_Y = parseInt(DROPDOWN_PADDING_Y, 10);
const fieldOrderTuples = [['RELEVANCE', 'DESC'], ['HUB_INSTALLS', 'DESC'], ['FIRST_PUBLISHED', 'DESC'], ['NAME', 'ASC'], ['NAME', 'DESC']];
const getOptionText = (field, order) => {
  switch (field) {
    case 'RELEVANCE':
      return unescapedText('marketplace-ui-apps-core.results.sort.options.relevance');
    case 'HUB_INSTALLS':
      return unescapedText('marketplace-ui-apps-core.results.sort.options.mostInstalled');
    case 'FIRST_PUBLISHED':
      return unescapedText('marketplace-ui-apps-core.results.sort.options.newest');
    case 'NAME':
      return order === 'ASC' ? unescapedText('marketplace-ui-apps-core.results.sort.options.name') : unescapedText('marketplace-ui-apps-core.results.sort.options.nameReverse');
    default:
      return null;
  }
};
const getOptionValue = (field, order) => `${field}:${order}`;
const SortSelectItem = ({
  children,
  option
}) => {
  return /*#__PURE__*/_jsx(UITooltip, {
    delay: 200,
    disabled: !option.tooltip,
    placement: "left",
    title: option.tooltip,
    children: children
  });
};
export default function SortDropdown({
  disabled,
  filters,
  isEmbeddedSort = false,
  isUngatedForRecommendations,
  onShowPreferredLanguageFirstChange,
  onSortChange
}) {
  const {
    search,
    showPreferredLangFirst,
    sortField,
    sortOrder
  } = filters;
  const handleChange = event => {
    const eventValue = event.target.value;
    const [field, order] = eventValue.split(':');
    onSortChange(field, order);
  };
  const [defaultField, defaultOrder] = useMemo(() => {
    if (isUngatedForRecommendations) {
      return ['RELEVANCE', 'DESC'];
    }
    return search ? ['RELEVANCE', 'DESC'] : ['HUB_INSTALLS', 'DESC'];
  }, [isUngatedForRecommendations, search]);
  const value = useMemo(() => {
    let field = defaultField;
    let order = defaultOrder;
    if (sortField) {
      field = sortField;
      order = sortOrder || defaultOrder;
    }
    return getOptionValue(field, order);
  }, [defaultField, defaultOrder, sortField, sortOrder]);
  const filterFieldOrderTuples = tuples => {
    return tuples.filter(([field]) => {
      if (field === 'RELEVANCE' && !isEmbeddedSort && !search && !isUngatedForRecommendations) {
        return false;
      }
      return true;
    });
  };
  const setDropdownOptions = options => {
    return options.map(([field, order]) => {
      const option = {
        value: getOptionValue(field, order),
        text: getOptionText(field, order),
        disabled: false,
        tooltip: null
      };
      return option;
    });
  };
  const filteredTuples = filterFieldOrderTuples(fieldOrderTuples);
  const options = setDropdownOptions(filteredTuples);
  const totalOptions = options.length;
  const minHeight = totalOptions * MENU_ITEM_HEIGHT + MENU_PADDING_Y * 2;
  const language = getLang();
  const showPreferredLangToggle = useMemo(() => /*#__PURE__*/_jsx(UICheckbox, {
    checked: showPreferredLangFirst,
    className: "m-top-1",
    onChange: ({
      target: {
        checked
      }
    }) => {
      if (onShowPreferredLanguageFirstChange) {
        onShowPreferredLanguageFirstChange(checked);
      }
    },
    size: "small",
    style: {
      maxWidth: 200
    },
    children: /*#__PURE__*/_jsx("span", {
      style: {
        lineHeight: '24px',
        position: 'relative',
        top: -3
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'marketplace-ui-apps-core.results.sort.showPreferredLangFirst',
        options: {
          language: unescapedText(`SharedI18nStrings.languageNames.${language}`)
        }
      })
    })
  }), [language, onShowPreferredLanguageFirstChange, showPreferredLangFirst]);
  return /*#__PURE__*/_jsx(UIInlineLabel, {
    className: "m-right-0",
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: 'marketplace-ui-apps-core.results.sort.label'
    }),
    children: /*#__PURE__*/_jsx(UISelect, {
      "data-test-id": "apps-sort-dropdown",
      buttonUse: "transparent",
      className: "p-right-0",
      disabled: disabled,
      dropdownFooter: onShowPreferredLanguageFirstChange ? showPreferredLangToggle : null,
      itemComponent: SortSelectItem,
      menuStyle: {
        minHeight
      },
      menuWidth: "auto",
      onChange: handleChange,
      options: options,
      value: value
    })
  });
}